import type React from 'react';

import { translationKey } from '@change-corgi/core/i18n';

import type { RecommendedGroup } from 'src/app/pages/starterDashboardV2/shared/types';

import type { RecommendedGroupWithPlatform } from '../../../../hooks/useSharingTools/types';
import { SharingToolsCardWrapper } from '../../../SharingToolsCardWrapper';
import { RecommendedGroupsCardsContainer } from '../RecommendedGroupCardsContainer';
import { RecommendedGroupsCardLoading } from '../RecommendedGroupsCardLoading';

type RecommendedGroupsCardPreviewProps = Readonly<{
	recommendedGroups: {
		isLoading: boolean;
		groups: readonly RecommendedGroupWithPlatform[];
	};
	primaryAction?: () => void;
	fallbackPrimaryAction?: () => void;
	onFacebookGroupClick?: (group: RecommendedGroup) => void;
	onRedditGroupClick?: (group: RecommendedGroup) => void;
}>;

export const RecommendedGroupsCardPreview: React.FC<RecommendedGroupsCardPreviewProps> = ({
	recommendedGroups,
	primaryAction = () => {},
	fallbackPrimaryAction = () => {},
	onFacebookGroupClick,
	onRedditGroupClick,
}) => {
	const { isLoading, groups } = recommendedGroups;

	const preview =
		isLoading || groups.length === 0 ? (
			<RecommendedGroupsCardLoading />
		) : (
			<RecommendedGroupsCardsContainer
				groups={groups}
				onFacebookGroupClick={onFacebookGroupClick}
				onRedditGroupClick={onRedditGroupClick}
			/>
		);

	const hasNoGroups = groups.length === 0 && !isLoading;
	const title = hasNoGroups
		? translationKey('corgi.petition.starter_dashboard_v2.sharing_tools.recommended_groups.title_no_groups')
		: translationKey('corgi.petition.starter_dashboard_v2.sharing_tools.recommended_groups.title');
	const description = hasNoGroups
		? translationKey('corgi.petition.starter_dashboard_v2.sharing_tools.recommended_groups.description_no_groups')
		: translationKey('corgi.petition.starter_dashboard_v2.sharing_tools.recommended_groups.description');

	const primaryActionText = hasNoGroups
		? translationKey(
				'corgi.petition.starter_dashboard_v2.sharing_tools.recommended_groups.primary_action_text_no_groups',
			)
		: translationKey('corgi.petition.starter_dashboard_v2.sharing_tools.recommended_groups.primary_action_text');

	const ctaAction = hasNoGroups ? fallbackPrimaryAction : primaryAction;

	return (
		<SharingToolsCardWrapper
			Preview={preview}
			titleKey={title}
			descriptionKey={description}
			primaryActionTextKey={primaryActionText}
			primaryActionOnClick={ctaAction}
		/>
	);
};
